import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown, Settings } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export const Header = ({ transparent = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getDashboardLink = () => {
    if (!user) return "/login";
    return user.user_type === "professional" ? "/pro/dashboard" : "/dashboard";
  };

  return (
    <header className={`fixed top-0 w-full z-50 ${transparent ? "bg-transparent" : "bg-white/95 backdrop-blur-md border-b border-slate-100"}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img 
              src="https://customer-assets-rejwkqb3.emergentagent.net/wingman/ec88479e-de0b-4b07-83a2-d0fc0e0593e3/attachments/a0930ce5021847efb76339185d8cd461_image.bin" 
              alt="BQL Leads" 
              className="h-10 md:h-12 w-auto"
              onError={(e) => { e.target.src = "/bql-logo.png"; }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/professionals" className="text-slate-600 hover:text-navy transition-colors font-medium" data-testid="nav-find-pros">
              Find Pros
            </Link>
            {/* Only show Pricing link for professionals or non-logged-in users (potential pros) */}
            {(!user || user?.user_type === "professional") && (
              <Link to="/pricing" className="text-slate-600 hover:text-navy transition-colors font-medium" data-testid="nav-pricing">
                Pro Pricing
              </Link>
            )}
            
            {user ? (
              <div className="flex items-center gap-4">
                <Link to={getDashboardLink()}>
                  <Button variant="outline" className="rounded-lg border-navy/20 hover:bg-navy/5 text-navy font-medium" data-testid="nav-dashboard-btn">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-lg" data-testid="user-menu-trigger">
                      <div className="w-9 h-9 rounded-lg bg-navy/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-navy" />
                      </div>
                      <ChevronDown className="w-4 h-4 ml-1 text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-lg shadow-lg">
                    <div className="px-3 py-3 bg-slate-50 rounded-t-lg">
                      <p className="font-semibold text-navy text-sm">{user.full_name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard" className="cursor-pointer">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings" className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Profile Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="rounded-lg font-medium text-slate-700 hover:text-navy" data-testid="nav-login-btn">
                    Log in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-navy hover:bg-navy-dark rounded-lg font-semibold shadow-sm" data-testid="nav-signup-btn">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 bg-white">
            <nav className="flex flex-col gap-1">
              <Link
                to="/professionals"
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Find Pros
              </Link>
              {/* Only show Pricing link for professionals or non-logged-in users */}
              {(!user || user?.user_type === "professional") && (
                <Link
                  to="/pricing"
                  className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pro Pricing
                </Link>
              )}
              
              {user ? (
                <>
                  <Link
                    to={getDashboardLink()}
                    className="px-4 py-3 text-navy font-semibold hover:bg-navy/5 rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/settings"
                    className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile Settings
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 text-red-600 text-left hover:bg-red-50 rounded-lg font-medium"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log in
                  </Link>
                  <div className="px-4 mt-2">
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button className="w-full bg-navy hover:bg-navy-dark rounded-lg font-semibold">
                        Get Started
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
